import { TaskValue } from 'lib/actions/task'
import { FC } from 'react'
import { IntlShape, useIntl } from 'react-intl'

export type AllowedActions =
    | 'close'
    | 'delete'
    | 'reassign'
    | 'approve'
    | 'deny'
    | 'comment'
    | 'provisionComplete'
    | 'provisionErrored'
    | undefined

const getApproveSuccessText = (intl: IntlShape, task?: TaskValue): string => {
    if (!task) {
        return intl.formatMessage({
            id: 'component_task_list_generic_approve_success',
            defaultMessage: 'Task approved',
        })
    }

    switch (task.type?.taskType.oneofKind) {
        case 'revoke':
            return intl.formatMessage({
                id: 'component_task_list_revoke_approve_success',
                defaultMessage: 'Revocation confirmed',
            })
        case 'certify':
            return intl.formatMessage({
                id: 'component_task_list_review_approve_success',
                defaultMessage: 'Access certified',
            })
        case 'action':
            return intl.formatMessage({
                id: 'component_task_list_action_approve_success',
                defaultMessage: 'Action approved',
            })
        case 'grant':
        default:
            return intl.formatMessage({
                id: 'component_task_list_request_approve_success',
                defaultMessage: 'Request approved',
            })
    }
}

const getDenySuccessText = (intl: IntlShape, task?: TaskValue): string => {
    if (!task) {
        return intl.formatMessage({
            id: 'component_task_list_generic_deny_success',
            defaultMessage: 'Task denied',
        })
    }

    switch (task.type?.taskType.oneofKind) {
        case 'revoke':
            return intl.formatMessage({
                id: 'component_task_list_revoke_deny_success',
                defaultMessage: 'Revocation declined',
            })
        case 'certify':
            return intl.formatMessage({
                id: 'component_task_list_review_deny_success',
                defaultMessage: 'Access recommended for removal',
            })
        case 'action':
            return intl.formatMessage({
                id: 'component_task_list_action_deny_success',
                defaultMessage: 'Action denied',
            })
        case 'grant':
        default:
            return intl.formatMessage({
                id: 'component_task_list_request_deny_success',
                defaultMessage: 'Request denied',
            })
    }
}

const getProvisionSuccessText = (intl: IntlShape, task?: TaskValue): string => {
    if (!task) {
        return intl.formatMessage({
            id: 'components_task_details_dropdown_provision_complete',
            defaultMessage: 'Marked provision complete',
        })
    }

    return task.type?.taskType.oneofKind === 'grant'
        ? intl.formatMessage({
            id: 'components_task_details_dropdown_provision_complete',
            defaultMessage: 'Marked provision complete',
        })
        : intl.formatMessage({
            id: 'components_task_details_dropdown_deprovision_complete',
            defaultMessage: 'Marked deprovision complete',
        })
}

const getProvisionErrorText = (intl: IntlShape, task?: TaskValue): string => {
    if (!task) {
        return intl.formatMessage({
            id: 'components_task_details_dropdown_provision_complete',
            defaultMessage: 'Marked won\'t do',
        })
    }

    return task.type?.taskType.oneofKind === 'grant'
        ? intl.formatMessage({
            id: 'components_task_details_dropdown_provision_complete',
            defaultMessage: 'Marked won\'t do',
        })
        : intl.formatMessage({
            id: 'components_task_details_dropdown_deprovision_error',
            defaultMessage: 'Marked won\'t do',
        })
}

export interface ApproveTextProps {
    task: TaskValue
    requiresStepUp?: boolean
}

export const ApproveText: FC<ApproveTextProps> = ({ task, requiresStepUp = false }): string => {
    const intl = useIntl()

    const getBaseText = (): string => {
        switch (task.type?.taskType.oneofKind) {
            case 'grant':
                return intl.formatMessage({
                    id: 'component_task_list_request_approve_option_label',
                    defaultMessage: 'Approve',
                })
            case 'revoke':
                return intl.formatMessage({
                    id: 'component_task_list_revoke_approve_option_label',
                    defaultMessage: 'Confirm',
                })
            case 'action':
                return intl.formatMessage({
                    id: 'component_task_list_action_approve_option_label',
                    defaultMessage: 'Approve',
                })
            case 'certify':
            default:
                return intl.formatMessage({
                    id: 'component_task_list_certify_approve_option_label',
                    defaultMessage: 'Certify',
                })
        }
    }

    if (!requiresStepUp) {
        return getBaseText()
    }

    return `${getBaseText()} ${intl.formatMessage({
        id: 'step_up_required_text',
        defaultMessage: '(step-up required)',
    })}`
}

export const getApproveText = (intl: IntlShape, task: TaskValue): string => {
    switch (task.type?.taskType.oneofKind) {
        case 'grant':
            return intl.formatMessage({
                id: 'component_task_list_request_approve_option_label',
                defaultMessage: 'Approve',
            })
        case 'revoke':
            return intl.formatMessage({
                id: 'component_task_list_revoke_approve_option_label',
                defaultMessage: 'Confirm',
            })
        case 'action':
            return intl.formatMessage({
                id: 'component_task_list_action_approve_option_label',
                defaultMessage: 'Approve',
            })
        case 'certify':
        default:
            return intl.formatMessage({
                id: 'component_task_list_certify_approve_option_label',
                defaultMessage: 'Certify',
            })
    }
}

export const getDenyText = (intl: IntlShape, task: TaskValue): string => {
    switch (task.type?.taskType.oneofKind) {
        case 'revoke':
            return intl.formatMessage({
                id: 'component_task_list_revoke_approve_option_label',
                defaultMessage: 'Decline',
            })
        case 'action':
        case 'grant':
        case 'certify':
        default:
            return intl.formatMessage({
                id: 'component_task_list_certify_approve_option_label',
                defaultMessage: 'Deny',
            })
    }
}

export const getSuccessMessage = ({
    actionType,
    intl,
    task,
}: {
    actionType: AllowedActions
    intl: IntlShape
    task?: TaskValue
}): string | React.ReactElement => {
    switch (actionType) {
        case 'close':
            return intl.formatMessage({
                id: 'components_task_details_dropdown_request_closed',
                defaultMessage: 'Comment closed',
            })
        case 'delete':
            return intl.formatMessage({
                id: 'components_task_details_dropdown_request_created',
                defaultMessage: 'Task deleted',
            })
        case 'reassign':
            return intl.formatMessage({
                id: 'components_task_details_dropdown_request_reassigned',
                defaultMessage: 'Task reassigned',
            })
        case 'approve':
            return getApproveSuccessText(intl, task)
        case 'deny':
            return getDenySuccessText(intl, task)
        case 'comment':
            return intl.formatMessage({
                id: 'components_task_details_dropdown_comment_created',
                defaultMessage: 'Comment added to task',
            })
        case 'provisionComplete':
            return getProvisionSuccessText(intl, task)
        case 'provisionErrored':
            return getProvisionErrorText(intl, task)
        default:
            return 'getSuccessMessage.default'
    }
}

export const getRequestAccessPresets = (intl: IntlShape): string[] => ([
    intl.formatMessage({
        id: 'request_access_preset_a',
        defaultMessage: 'Needed for my current role',
    }),
    intl.formatMessage({
        id: 'request_access_preset_b',
        defaultMessage: 'I\'m a new employee',
    }),
    intl.formatMessage({
        id: 'request_access_preset_c',
        defaultMessage: 'I\'m a new contractor',
    }),
    intl.formatMessage({
        id: 'request_access_preset_d',
        defaultMessage: 'My role has changed',
    }),
    intl.formatMessage({
        id: 'request_access_preset_e',
        defaultMessage: 'Needed for testing',
    }),
    intl.formatMessage({
        id: 'request_access_preset_f',
        defaultMessage: 'Needed for project (provide name)',
    }),
])

export const getApprovePresets = (intl: IntlShape): string[] => ([
    intl.formatMessage({
        id: 'approve_preset_a',
        defaultMessage: 'Needed for current role',
    }),
    intl.formatMessage({
        id: 'approve_preset_b',
        defaultMessage: 'New employee',
    }),
    intl.formatMessage({
        id: 'approve_preset_c',
        defaultMessage: 'New contractor',
    }),
    intl.formatMessage({
        id: 'approve_preset_d',
        defaultMessage: 'Employee\'s role has changed',
    }),
    intl.formatMessage({
        id: 'approve_preset_e',
        defaultMessage: 'Needed for testing',
    }),
    intl.formatMessage({
        id: 'approve_preset_f',
        defaultMessage: 'Needed for project (provide name)',
    }),
])

export const getCertifyPresets = (intl: IntlShape): string[] => ([
    intl.formatMessage({
        id: 'certify_preset_a',
        defaultMessage: 'Needed for current role',
    }),
    intl.formatMessage({
        id: 'certify_preset_b',
        defaultMessage: 'Required for new role',
    }),
    intl.formatMessage({
        id: 'certify_preset_c',
        defaultMessage: 'Required for project (provide name)',
    }),
    intl.formatMessage({
        id: 'certify_preset_d',
        defaultMessage: 'Required for maintenance',
    }),
])

export const getConfirmPresets = (intl: IntlShape): string[] => ([
    intl.formatMessage({
        id: 'confirm_preset_a',
        defaultMessage: 'No longer needed',
    }),
    intl.formatMessage({
        id: 'confirm_preset_b',
        defaultMessage: 'Employee changing roles',
    }),
    intl.formatMessage({
        id: 'confirm_preset_c',
        defaultMessage: 'Employee leaving',
    }),
    intl.formatMessage({
        id: 'confirm_preset_d',
        defaultMessage: 'Contractor termination',
    }),
    intl.formatMessage({
        id: 'confirm_preset_e',
        defaultMessage: 'Unused access',
    }),
    intl.formatMessage({
        id: 'confirm_preset_f',
        defaultMessage: 'Approved request was in error',
    }),
    intl.formatMessage({
        id: 'confirm_preset_g',
        defaultMessage: 'Approved request was suspicious',
    }),
])

export const getDenyPresets = (intl: IntlShape): string[] => ([
    intl.formatMessage({
        id: 'deny_preset_a',
        defaultMessage: 'Not needed for current role',
    }),
    intl.formatMessage({
        id: 'deny_preset_b',
        defaultMessage: 'Requires additional approvals',
    }),
    intl.formatMessage({
        id: 'deny_preset_c',
        defaultMessage: 'Insufficient justification',
    }),
    intl.formatMessage({
        id: 'deny_preset_d',
        defaultMessage: 'Entitlement not yet ready',
    }),
    intl.formatMessage({
        id: 'deny_preset_e',
        defaultMessage: 'Need more information',
    }),
    intl.formatMessage({
        id: 'deny_preset_f',
        defaultMessage: 'Duplicate request',
    }),
])

export const getRemovePresets = (intl: IntlShape): string[] => ([
    intl.formatMessage({
        id: 'remove_preset_a',
        defaultMessage: 'No longer needed',
    }),
    intl.formatMessage({
        id: 'remove_preset_b',
        defaultMessage: 'Role has changed',
    }),
    intl.formatMessage({
        id: 'remove_preset_c',
        defaultMessage: 'Unused access',
    }),
    intl.formatMessage({
        id: 'remove_preset_d',
        defaultMessage: 'Violates policy',
    }),
])

export const getDeclinePresets = (intl: IntlShape): string[] => ([
    intl.formatMessage({
        id: 'decline_preset_a',
        defaultMessage: 'User still needs this access',
    }),
    intl.formatMessage({
        id: 'decline_preset_b',
        defaultMessage: 'Required by role',
    }),
])

export const getReassignPresets = (intl: IntlShape): string[] => ([
    intl.formatMessage({
        id: 'reassign_preset_a',
        defaultMessage: 'Better suited for another reviewer',
    }),
    intl.formatMessage({
        id: 'reassign_preset_b',
        defaultMessage: 'Not my responsibility',
    }),
    intl.formatMessage({
        id: 'reassign_preset_c',
        defaultMessage: 'I am unavailable',
    }),
    intl.formatMessage({
        id: 'reassign_preset_d',
        defaultMessage: 'Reassigned due to approver unresponsive',
    }),
])

export const getSelfReassignPresets = (intl: IntlShape): string[] => ([
    intl.formatMessage({
        id: 'self_reassign_preset_a',
        defaultMessage: 'I am available',
    }),
    intl.formatMessage({
        id: 'self_reassign_preset_b',
        defaultMessage: 'I am taking this over',
    }),
    intl.formatMessage({
        id: 'self_reassign_preset_c',
        defaultMessage: 'This is my responsibility',
    }),
])
